import React, { useEffect, useRef, useState } from 'react';
import { parseAreas } from '../../utils/crmImport';
import '../common/AddressAutocomplete.css';
import './AreasInput.css';

const GOOGLE_PLACES_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';
export const MAX_AREAS = 10;

/** "Oakland" + "CA, USA" -> "Oakland, CA". */
const labelFor = (prediction) => {
  const main = prediction.structuredFormat?.mainText?.text || prediction.text?.text || '';
  const state = (prediction.structuredFormat?.secondaryText?.text || '').split(',')[0].trim();
  return state && state.length <= 3 ? `${main}, ${state}` : main;
};

const suggestCities = async (input) => {
  const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': GOOGLE_PLACES_KEY },
    body: JSON.stringify({ input, includedPrimaryTypes: ['(cities)'], includedRegionCodes: ['us'], languageCode: 'en' }),
  });
  if (!res.ok) throw new Error(`Places autocomplete failed: ${res.status}`);
  const data = await res.json();
  return (data.suggestions || []).map((s) => s.placePrediction).filter(Boolean).map(labelFor).filter(Boolean).slice(0, 5);
};

/**
 * Where a buyer is looking: any number of cities, neighborhoods or ZIP codes, shown as removable
 * chips. Google suggests cities as you type; anything else (a neighborhood, a ZIP code) is added by
 * typing it and pressing Enter. Typed text that is still waiting when the field loses focus is added
 * too, so nothing is lost when the form is saved.
 */
const AreasInput = ({ value, onChange, max = MAX_AREAS, ariaLabel = 'Add a city or area' }) => {
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);
  const requestId = useRef(0);
  const full = value.length >= max;

  useEffect(() => () => clearTimeout(debounce.current), []);

  const add = (labels) => {
    const next = [...value];
    labels.forEach((label) => {
      const clean = String(label || '').trim();
      if (clean && next.length < max && !next.some((v) => v.toLowerCase() === clean.toLowerCase())) next.push(clean);
    });
    if (next.length !== value.length) onChange(next);
    setText('');
    setSuggestions([]);
  };

  const handleChange = (e) => {
    const typed = e.target.value;
    setText(typed);
    setSuggestions([]);
    clearTimeout(debounce.current);
    if (!GOOGLE_PLACES_KEY || typed.trim().length < 2) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const mine = ++requestId.current;
    debounce.current = setTimeout(async () => {
      try {
        const found = await suggestCities(typed.trim());
        if (mine === requestId.current) setSuggestions(found.filter((f) => !value.some((v) => v.toLowerCase() === f.toLowerCase())));
      } catch (err) {
        console.warn('City suggestions unavailable:', err.message);   // typing and Enter still work
      } finally {
        if (mine === requestId.current) setLoading(false);
      }
    }, 300);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ';') {
      e.preventDefault();               // Enter must not submit the surrounding form
      if (text.trim()) add(parseAreas(text));
    } else if (e.key === 'Backspace' && text === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (text.trim()) add(parseAreas(text));
  };

  return (
    <div className="areas-input">
      {value.length > 0 && (
        <ul className="areas-chips" aria-label="Areas of interest">
          {value.map((area) => (
            <li key={area} className="areas-chip">
              <span>{area}</span>
              <button type="button" aria-label={`Remove ${area}`} onClick={() => onChange(value.filter((v) => v !== area))}>×</button>
            </li>
          ))}
        </ul>
      )}
      <div className="addr-ac-wrapper">
        <input
          type="text"
          className="crm-input"
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={full ? `You can add up to ${max} areas` : value.length ? 'Add another city or area' : 'Start typing a city, then pick it'}
          aria-label={ariaLabel}
          disabled={full}
          autoComplete="off"
        />
        {loading && <div className="addr-ac-spinner" />}
        {suggestions.length > 0 && (
          <div className="addr-ac-dropdown">
            {suggestions.map((label, i) => (
              <button
                key={label}
                type="button"
                className={`addr-ac-item${i < suggestions.length - 1 ? ' addr-ac-item--border' : ''}`}
                onMouseDown={(e) => e.preventDefault()}   // keep focus, so picking does not also trigger the blur
                onClick={() => add([label])}
              >
                <svg className="addr-ac-pin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                </svg>
                <div className="addr-ac-text"><span className="addr-ac-main">{label}</span></div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AreasInput;
